// ============================================================
// RulerPicker.jsx
// Caminho: src/components/plataforma/pessoal/forms/components/RulerPicker.jsx
// ============================================================
import React, { useEffect, useRef } from "react";
import styles from "./RulerPicker.module.scss";

export default function RulerPicker({
  axis = "x",
  min = 0,
  max = 100,
  step = 1,
  decimals = 0,
  majorEvery = 5,
  initialValue,
  onChange,
  refValue = null,
  showBand = false,
}) {
  const scrollRef     = useRef(null);
  const trackRef      = useRef(null);
  const ticksRef      = useRef([]);
  const animRef       = useRef(null);
  const momRef        = useRef(null);
  const valueRef      = useRef(initialValue ?? min); // fonte da verdade
  const refValueRef   = useRef(refValue);
  const updateBandRef = useRef(null);

  const isV = axis === "y";

  useEffect(() => {
    const scroll = scrollRef.current;
    const track  = trackRef.current;
    if (!scroll || !track) return;

    const ticks = [];
    ticksRef.current = ticks;
    const created = [];

    // 1. BUILD TICKS
    const spacerStart = document.createElement("div");
    spacerStart.className = styles.spacer;
    track.appendChild(spacerStart);
    created.push(spacerStart);

    const count         = Math.round(Math.abs((max - min) / step));
    const majorInterval = Math.max(1, Math.round(Math.abs(majorEvery / step)));

    for (let i = 0; i <= count; i++) {
      const tick = document.createElement("div");
      tick.className = isV ? styles.tickV : styles.tick;

      if (i % majorInterval === 0) {
        tick.classList.add(isV ? styles.tickVMajor : styles.tickMajor);
        const label = document.createElement("span");
        label.className = isV ? styles.tickLabelV : styles.tickLabel;
        const v = +(min + i * step).toFixed(decimals);
        label.textContent =
          decimals > 0 ? String(v).replace(".", ",") : String(Math.round(v));
        tick.appendChild(label);
      }

      ticks.push(tick);
      track.appendChild(tick);
      created.push(tick);
    }

    const spacerEnd = document.createElement("div");
    spacerEnd.className = styles.spacer;
    track.appendChild(spacerEnd);
    created.push(spacerEnd);

    // Band (somente horizontal)
    let bandEl = null;
    if (showBand && !isV) {
      bandEl = document.createElement("div");
      bandEl.className = styles.band;
      track.appendChild(bandEl);
      created.push(bandEl);
    }

    // Helpers
    const viewport  = () => (isV ? scroll.clientHeight : scroll.clientWidth);
    const scrollPos = () => (isV ? scroll.scrollTop : scroll.scrollLeft);
    const center    = (t) =>
      isV ? t.offsetTop + t.offsetHeight / 2 : t.offsetLeft + t.offsetWidth / 2;

    function setScroll(p) {
      const maxScroll = isV
        ? scroll.scrollHeight - scroll.clientHeight
        : scroll.scrollWidth - scroll.clientWidth;
      p = Math.max(0, Math.min(maxScroll, p));
      if (isV) scroll.scrollTop = p;
      else scroll.scrollLeft = p;
    }

    function layout() {
      const half = viewport() / 2;
      if (isV) {
        spacerStart.style.height = half + "px";
        spacerEnd.style.height = half + "px";
      } else {
        spacerStart.style.width = half + "px";
        spacerEnd.style.width = half + "px";
      }
    }

    function isReady() {
      return (
        viewport() > 0 &&
        ticks.length > 1 &&
        center(ticks[1]) - center(ticks[0]) > 0
      );
    }

    function nearestIndex() {
      const c = scrollPos() + viewport() / 2;
      const f = center(ticks[0]);
      const pitch = center(ticks[1]) - f;
      return Math.max(0, Math.min(ticks.length - 1, Math.round((c - f) / pitch)));
    }

    function emit() {
      if (!isReady()) return; // nunca emite antes do layout estar pronto
      const v = +(min + nearestIndex() * step).toFixed(decimals);
      valueRef.current = v;
      onChange && onChange(v);
    }

    function animateTo(target) {
      cancelAnimationFrame(animRef.current);
      cancelAnimationFrame(momRef.current);
      const start = scrollPos();
      const dist = target - start;
      const dur = 230;
      const t0 = performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      const f = (now) => {
        const t = Math.min(1, (now - t0) / dur);
        setScroll(start + dist * ease(t));
        emit();
        if (showBand) updateBand();
        if (t < 1) animRef.current = requestAnimationFrame(f);
      };
      animRef.current = requestAnimationFrame(f);
    }

    function snap() {
      const i = nearestIndex();
      animateTo(center(ticks[i]) - viewport() / 2);
    }

    function positionTo(v) {
      const i = Math.max(0, Math.min(ticks.length - 1, Math.round((v - min) / step)));
      setScroll(center(ticks[i]) - viewport() / 2);
    }

    function setValue(v, animate = true) {
      const lo = Math.min(min, max);
      const hi = Math.max(min, max);
      v = Math.max(lo, Math.min(hi, +v.toFixed(decimals)));
      valueRef.current = v;
      const i = Math.round((v - min) / step);
      const target = center(ticks[i]) - viewport() / 2;
      if (animate) animateTo(target);
      else {
        setScroll(target);
        emit();
      }
    }

    function posOf(v) {
      const i = Math.max(0, Math.min(ticks.length - 1, Math.round((v - min) / step)));
      return center(ticks[i]);
    }

    function updateBand() {
      if (!bandEl || refValueRef.current == null || !isReady()) return;
      const ref = refValueRef.current;
      const pRef = posOf(ref);
      const pVal = posOf(valueRef.current);
      bandEl.style.left = Math.min(pRef, pVal) + "px";
      bandEl.style.width = Math.abs(pVal - pRef) + "px";
      const iRef = Math.max(0, Math.min(ticks.length - 1, Math.round((ref - min) / step)));
      ticks.forEach((t, idx) => t.classList.toggle(styles.tickRef, idx === iRef));
    }
    updateBandRef.current = updateBand;

    // EVENTOS
    let dragging = false;
    let startPos = 0;
    let startScrl = 0;
    let vel = 0;
    let lastPos = 0;
    let lastTime = 0;

    const evtPos = (e) =>
      isV
        ? e.touches ? e.touches[0].clientY : e.clientY
        : e.touches ? e.touches[0].clientX : e.clientX;

    function onPointerDown(e) {
      cancelAnimationFrame(animRef.current);
      cancelAnimationFrame(momRef.current);
      dragging = true;
      startPos = evtPos(e);
      startScrl = scrollPos();
      vel = 0;
      lastPos = startPos;
      lastTime = performance.now();
      if (scroll.setPointerCapture) scroll.setPointerCapture(e.pointerId);
      e.preventDefault();
      scroll.classList.add(styles.grabbing);
    }

    function onPointerMove(e) {
      if (!dragging) return;
      const cur = evtPos(e);
      setScroll(startScrl - (cur - startPos));
      const now = performance.now();
      const dt = now - lastTime;
      if (dt > 0) vel = (cur - lastPos) / dt;
      lastPos = cur;
      lastTime = now;
      emit();
      if (showBand) updateBand();
    }

    function onPointerUp(e) {
      if (!dragging) return;
      dragging = false;
      if (scroll.releasePointerCapture) scroll.releasePointerCapture(e.pointerId);
      scroll.classList.remove(styles.grabbing);

      if (Math.abs(vel * 14) > 0.5) {
        let v = vel;
        const decay = 0.88;
        const mom = () => {
          setScroll(scrollPos() - v * 14);
          emit();
          if (showBand) updateBand();
          v *= decay;
          if (Math.abs(v) > 0.3) momRef.current = requestAnimationFrame(mom);
          else snap();
        };
        momRef.current = requestAnimationFrame(mom);
      } else {
        snap();
      }
    }

    let wheelTimer = null;
    function onWheel(e) {
      e.preventDefault();
      setScroll(scrollPos() + (e.deltaX || e.deltaY));
      emit();
      if (showBand) updateBand();
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(snap, 130);
    }

    function onKeyDown(e) {
      const inc = e.key === "ArrowRight" || e.key === "ArrowUp";
      const dec = e.key === "ArrowLeft" || e.key === "ArrowDown";
      if (!inc && !dec) return;
      e.preventDefault();
      setValue(valueRef.current + (inc ? step : -step));
    }

    scroll.addEventListener("pointerdown", onPointerDown, { passive: false });
    scroll.addEventListener("pointermove", onPointerMove, { passive: false });
    scroll.addEventListener("pointerup", onPointerUp);
    scroll.addEventListener("pointercancel", onPointerUp);
    scroll.addEventListener("wheel", onWheel, { passive: false });
    scroll.addEventListener("keydown", onKeyDown);

    // INIT — só posiciona quando o layout estiver realmente pronto
    let initialized = false;
    let rafId = null;

    function tryInit() {
      if (initialized) return;
      if (!isReady()) {
        rafId = requestAnimationFrame(tryInit);
        return;
      }
      layout();
      positionTo(valueRef.current);
      if (showBand) updateBand();
      initialized = true;
    }
    rafId = requestAnimationFrame(tryInit);

    const ro = new ResizeObserver(() => {
      if (!initialized) {
        tryInit();
        return;
      }
      layout();
      positionTo(valueRef.current);
      if (showBand) updateBand();
    });
    ro.observe(scroll);

    // CLEANUP
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(wheelTimer);
      cancelAnimationFrame(animRef.current);
      cancelAnimationFrame(momRef.current);
      ro.disconnect();
      scroll.removeEventListener("pointerdown", onPointerDown);
      scroll.removeEventListener("pointermove", onPointerMove);
      scroll.removeEventListener("pointerup", onPointerUp);
      scroll.removeEventListener("pointercancel", onPointerUp);
      scroll.removeEventListener("wheel", onWheel);
      scroll.removeEventListener("keydown", onKeyDown);
      created.forEach((n) => n.parentNode?.removeChild(n));
      ticksRef.current = [];
      updateBandRef.current = null;
    };
  }, []);

  // Atualiza banda quando refValue muda
  useEffect(() => {
    refValueRef.current = refValue;
    if (showBand) updateBandRef.current?.();
  }, [refValue, showBand]);

  if (isV) {
    return (
      <div className={styles.wrapperV}>
        <div ref={scrollRef} className={styles.scrollV} tabIndex={0} data-no-swipe>
          <div ref={trackRef} className={styles.trackV} />
        </div>
        <div className={styles.needleV} />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div ref={scrollRef} className={styles.scroll} tabIndex={0} data-no-swipe>
        <div ref={trackRef} className={styles.track} />
      </div>
      <div className={styles.needle} />
    </div>
  );
}